import { Badge, Group, ScrollArea, UnstyledButton } from '@mantine/core'
import type { ReactNode } from 'react'

export type PillNavItem<T extends string = string> = {
  value: T
  label: string
  icon?: ReactNode
  count?: number
  countColor?: string
  disabled?: boolean
}

export function PillNav<T extends string>({
  value,
  onChange,
  items,
}: {
  value: T
  onChange: (v: T) => void
  items: PillNavItem<T>[]
}) {
  return (
    <ScrollArea type="scroll" scrollbarSize={0}>
      <Group gap="xs" wrap="nowrap" pb={2}>
        {items.map((item) => {
          const active = value === item.value
          return (
            <UnstyledButton
              key={item.value}
              onClick={() => !item.disabled && onChange(item.value)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 14px',
                borderRadius: 999,
                fontSize: '0.825rem',
                fontWeight: active ? 600 : 500,
                whiteSpace: 'nowrap',
                transition: 'background 140ms ease, color 140ms ease',
                background: active ? 'var(--mantine-color-laut-6)' : 'transparent',
                color: active ? 'white' : item.disabled ? 'var(--mantine-color-dimmed)' : 'var(--mantine-color-dimmed)',
                opacity: item.disabled ? 0.45 : 1,
                cursor: item.disabled ? 'not-allowed' : 'pointer',
              }}
              onMouseEnter={(e) => {
                if (!active && !item.disabled)
                  (e.currentTarget as HTMLElement).style.background = 'var(--mantine-color-laut-0)'
              }}
              onMouseLeave={(e) => {
                if (!active) (e.currentTarget as HTMLElement).style.background = 'transparent'
              }}
            >
              {item.icon && <span style={{ opacity: active ? 1 : 0.7, display: 'flex' }}>{item.icon}</span>}
              {item.label}
              {item.count !== undefined && item.count > 0 && (
                <Badge
                  size="xs"
                  circle
                  color={active ? 'laut' : (item.countColor ?? 'gray')}
                  variant={active ? 'white' : 'filled'}
                  style={{ minWidth: 18 }}
                >
                  {item.count}
                </Badge>
              )}
            </UnstyledButton>
          )
        })}
      </Group>
    </ScrollArea>
  )
}
